import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/simple/button'
import { NumberSection } from '../../layouts/components/numberSection'
import { SpecialPanel } from '../../layouts/components/panel'
import { SimpleLayout, SimpleMessage } from '../../layouts/simple'
import { School, Teacher, useAppContext } from '../../providers/context'
import React from 'react'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import { BasicUser } from '../../components/userSelector/users'
import { Loader } from '../../components/simple/loader'
import { UserSelector } from '../../components/userSelector/userSelector'
import { TextInput } from '../../components/form/textInput'

type OnboardingData = {
  schoolAdmins: BasicUser[]
  phoneNumber: string
  districtPhoneNumber: string
  abbreviation: string
}

export const SchoolOnboardingPage: React.FC<{ user: Teacher }> = ({ user }) => {
  if (user.schools.length !== 1) return <SimpleMessage linkToHome>Invalid School</SimpleMessage>
  const school = user.schools[0]

  const { data: onboardingData } = useRequest<OnboardingData>('/frontend_api/getSchoolOnboarding')

  return (
    <SimpleLayout>
      {onboardingData ? (
        <SchoolOnboardingPanel onboardingData={onboardingData} user={user} school={school} />
      ) : (
        <Loader className="mt-8" />
      )}
    </SimpleLayout>
  )
}

export const SchoolOnboardingPanel: React.FC<{ onboardingData: OnboardingData; user: Teacher; school: School }> = ({
  user,
  onboardingData,
  school,
}) => {
  const { state, reloadUser } = useAppContext()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)

  const [schoolAdmins, setSchoolAdmins] = React.useState<BasicUser[]>(
    onboardingData.schoolAdmins.map((user) => ({ ...user, notToggleable: true }))
  )
  const [allClassUsers, setAllClassUsers] = React.useState<BasicUser[]>([])
  const [gradeGroupUsers, setGradeGroupUsers] = React.useState<BasicUser[]>([])
  const [phoneNumber, setPhoneNumber] = React.useState(onboardingData.phoneNumber)
  const [abbreviation, setAbbreviation] = React.useState(onboardingData.abbreviation)

  const save = () => {
    setLoading(true)

    makeRequest('/frontend_api/completeSchoolOnboarding', {
      schoolAdmins: schoolAdmins.map((user) => user.id),
      allClassUsers: allClassUsers.map((user) => user.id),
      gradeGroupUsers: gradeGroupUsers.map((user) => user.id),
      phoneNumber,
      abbreviation,
    })
      .then(() => reloadUser().then(() => navigate('/dashboard')))
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  return (
    <SpecialPanel className="mb-8 sm:!w-[650px]">
      <div className="text-3xl font-semibold">Wecome to {state.appName}!</div>
      <div className="-mt-1">Please complete a few steps to configure {school.name}</div>
      <div className="-mt-2 text-sm">Fields below are optional</div>
      <NumberSection
        className="my-4"
        number={1}
        title={
          <>
            Appoint <b>assistant principals</b> and other <b>admins</b> at your school
          </>
        }
      >
        <UserSelector
          selectedUsers={schoolAdmins}
          setSelectedUsers={setSchoolAdmins}
          placeholder="Search for users"
          url={`/frontend_api/schoolOnboardingUserSearch`}
        />
      </NumberSection>
      <NumberSection
        className="my-4"
        number={2}
        title={
          <>
            Appoint <b>nurses</b> at your school
          </>
        }
        subtitle={'These users will be granted access to see all classes at your school'}
      >
        <UserSelector
          selectedUsers={allClassUsers}
          setSelectedUsers={setAllClassUsers}
          placeholder="Search for users"
          url={`/frontend_api/schoolOnboardingUserSearch`}
        />
      </NumberSection>
      <NumberSection
        className="my-4"
        number={3}
        title={
          <>
            Appoint <b>administrative staff</b>, <b>guidance counselors</b>, <b>athletic directors</b>, and other{' '}
            <b>program directors</b> at your school
          </>
        }
        subtitle={'These users will be granted access to send to entire grades at your school'}
      >
        <UserSelector
          selectedUsers={gradeGroupUsers}
          setSelectedUsers={setGradeGroupUsers}
          placeholder="Search for users"
          url={`/frontend_api/schoolOnboardingUserSearch`}
        />
      </NumberSection>
      <NumberSection
        className="my-4"
        number={4}
        title={'Set the abbreviation that will be used to refer to this school in outgoing messages.'}
        subtitle={
          'This should be a short abbreviation of your school name. Leave this field as-is if you are satisfied with the default shown below.'
        }
      >
        <TextInput
          className="rounded-full"
          value={abbreviation}
          onChange={setAbbreviation}
          placeholder="School Abbreviation"
        />
      </NumberSection>
      <NumberSection
        className="my-4"
        number={5}
        title={'Set the phone number that will be used to send messages from your school'}
      >
        <TextInput
          className="rounded-full"
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder={
            onboardingData.districtPhoneNumber
              ? `inheriting ${onboardingData.districtPhoneNumber} from district`
              : 'xxx-xxx-xxxx'
          }
        />
      </NumberSection>
      <Button logo onClick={save} disabled={loading} showLoader={loading}>
        Save and Continue
      </Button>
    </SpecialPanel>
  )
}
